import { Configuration, PopupRequest } from "@azure/msal-browser";

// Get configuration from runtime environment (Azure App Service settings)
// Falls back to process.env for local development
declare global {
  interface Window {
    ENV_CONFIG?: {
      AZURE_CLIENT_ID: string;
      AZURE_TENANT_ID: string;
      REDIRECT_URI: string;
      POST_LOGOUT_REDIRECT_URI: string;
      GRAPH_ME_ENDPOINT: string;
      GRAPH_PHOTO_ENDPOINT: string;
      API_APP_ID_URI?: string;
      DISABLE_SSO?: string | boolean;
      BACKEND_REQUIRES_AUTH?: string | boolean;
      ENABLE_BACKEND_TOKEN_CACHE?: string | boolean;
    };
  }
}

export const getConfig = () => {
  // In production (Azure), use runtime config from window.ENV_CONFIG
  // In development, use process.env
  if (window.ENV_CONFIG) {
    return window.ENV_CONFIG;
  }
  
  return {
    AZURE_CLIENT_ID: process.env.REACT_APP_AZURE_CLIENT_ID || "",
    AZURE_TENANT_ID: process.env.REACT_APP_AZURE_TENANT_ID || "",
    REDIRECT_URI: process.env.REACT_APP_REDIRECT_URI || "http://localhost:3000",
    POST_LOGOUT_REDIRECT_URI: process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI || "http://localhost:3000",
    GRAPH_ME_ENDPOINT: process.env.REACT_APP_GRAPH_ME_ENDPOINT || "https://graph.microsoft.com/v1.0/me",
    GRAPH_PHOTO_ENDPOINT: process.env.REACT_APP_GRAPH_PHOTO_ENDPOINT || "https://graph.microsoft.com/v1.0/me/photo/$value",
    API_APP_ID_URI: process.env.REACT_APP_API_APP_ID_URI || "",
    // DISABLE_SSO removed: SSO/OIDC disable logic is no longer needed.
    BACKEND_REQUIRES_AUTH: process.env.REACT_APP_BACKEND_REQUIRES_AUTH || "false",
    ENABLE_BACKEND_TOKEN_CACHE: process.env.REACT_APP_ENABLE_BACKEND_TOKEN_CACHE || "false"
  };
};

const config = getConfig();
// isSsoDisabled removed: SSO/OIDC disable logic is no longer needed.

const normalizeRedirectUri = (uri: string) => uri.replace(/\/+$/, "");

const redirectUri = normalizeRedirectUri(config.REDIRECT_URI);
const postLogoutRedirectUri = normalizeRedirectUri(config.POST_LOGOUT_REDIRECT_URI);

// MSAL configuration - using runtime configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: config.AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${config.AZURE_TENANT_ID}`,
    redirectUri,
    postLogoutRedirectUri
  },
  
cache: {
     cacheLocation: "sessionStorage"
  }

};

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest: PopupRequest = {
  scopes: ["User.Read"],
};

// Scopes to acquire when calling the backend API.
// Uses explicit API App ID URI when provided (recommended for migrated/new app registrations).
// NOTE: If SSO is disabled, this will be ignored in apiFetch()
export function getApiScopes(): string[] {
  const runtimeConfig = getConfig();
  const ssoDisabled = runtimeConfig.DISABLE_SSO === true || runtimeConfig.DISABLE_SSO === "true";
  
  if (ssoDisabled) {
    return [];
  }
  
  const apiAppIdUri = runtimeConfig.API_APP_ID_URI || `api://${runtimeConfig.AZURE_CLIENT_ID}/user_impersonation`;
  // If API_APP_ID_URI already contains the scope (has a second path segment), use it directly
  if (apiAppIdUri.split('/').length > 3) {
    return [apiAppIdUri];
  }
  return [`${apiAppIdUri}/user_impersonation`];
}

export const API_SCOPES = getApiScopes();

// Add here the endpoints for MS Graph API services you would like to use.
export const graphConfig = {
  graphMeEndpoint: config.GRAPH_ME_ENDPOINT,
  graphPhotoEndpoint: config.GRAPH_PHOTO_ENDPOINT,
};
